import {
  Bell,
  BookOpen,
  Boxes,
  Cable,
  FileBarChart,
  IdCard,
  Images,
  KeyRound,
  Library,
  LogIn,
  Mails,
  MessagesSquare,
  Palette,
  Plug,
  RefreshCw,
  Send,
  Server,
  Settings2,
  ShieldCheck,
  SwatchBook,
  Type,
  UserPlus,
  Video,
} from 'lucide-react'
import { ROLES } from '../../app/navigation'
import { settingsStrings as S } from './strings'

const N = S.nav

/**
 * ONE MAP OF EVERY SETTING IN THE PRODUCT.
 *
 * Settings live on two pages — `/settings` and `/settings/advanced` — each with its own
 * loader and its own panels, and each used to show only its own sections: eleven in a
 * strip that wrapped to two lines on one, seven on the other, and a link between them. To
 * a person looking for "where do I change the SMTP host" the split was invisible and the
 * strip was a crowd.
 *
 * This is the whole of it, grouped by what the person is doing rather than by which page
 * happens to hold the form. Each page draws its two rows of tabs — the groups, then the
 * chosen group's sections — from ITS OWN part of this map (SettingsTabs, 2026-10-22: the
 * two pages are two menu entries, and showing every group on both repeated their links).
 * Neither page's URL or hash changes — `/settings#media` and `/settings/advanced#login`
 * land exactly where they always did.
 *
 * Labels are nouns. The page is already called Settings; "Reports settings" under it
 * said the word twice.
 */
export const SETTINGS_NAV = [
  {
    id: 'appearance',
    title: N.groups.appearance,
    icon: SwatchBook,
    items: [
      { id: 'identity', page: 'main', label: N.identity, icon: Palette },
      { id: 'media', page: 'main', label: N.media, icon: Images },
      { id: 'keys', page: 'main', label: N.keys, icon: Type },
      { id: 'contact', page: 'main', label: N.contact, icon: Mails },
    ],
  },
  {
    id: 'content',
    title: N.groups.content,
    icon: BookOpen,
    items: [
      { id: 'video', page: 'main', label: N.video, icon: Video },
      { id: 'userManual', page: 'main', label: N.userManual, icon: BookOpen },
      { id: 'documentation', page: 'main', label: N.documentation, icon: Library },
    ],
  },
  {
    id: 'communication',
    title: N.groups.communication,
    icon: Send,
    items: [
      { id: 'notifications', page: 'main', label: N.notifications, icon: Bell },
      { id: 'email', page: 'main', label: N.email, icon: Send },
      { id: 'messaging', page: 'main', label: N.messaging, icon: MessagesSquare },
      { id: 'reports', page: 'main', label: N.reports, icon: FileBarChart },
    ],
  },
  {
    id: 'access',
    title: N.groups.access,
    icon: ShieldCheck,
    items: [
      { id: 'login', page: 'advanced', label: N.login, icon: LogIn },
      { id: 'registration', page: 'advanced', label: N.registration, icon: UserPlus },
      { id: 'profile', page: 'advanced', label: N.profile, icon: IdCard },
    ],
  },
  {
    id: 'integration',
    title: N.groups.integration,
    icon: Cable,
    items: [
      { id: 'sources', page: 'advanced', label: N.sources, icon: Plug },
      { id: 'sync', page: 'advanced', label: N.sync, icon: RefreshCw },
      { id: 'servers', page: 'advanced', label: N.servers, icon: Server },
      { id: 'services', page: 'advanced', label: N.services, icon: Boxes },
    ],
  },
  {
    id: 'system',
    title: N.groups.system,
    icon: Settings2,
    items: [
      {
        id: 'settingsKeys',
        to: '/settings/keys',
        label: N.settingsKeys,
        icon: KeyRound,
        role: ROLES.AdvancedSettingsManagers,
      },
    ],
  },
]

const PAGE_PATH = { main: '/settings', advanced: '/settings/advanced' }

/* The role each page's API answers to — the same pair the main menu gates these screens
   on (`settingsGroup` in app/navigation.js). */
const PAGE_ROLE = { main: ROLES.SettingsManagers, advanced: ROLES.AdvancedSettingsManagers }

/**
 * The rail as one page sees it: its own sections as panels, everything else as links.
 *
 * ONLY WHAT THE READER CAN OPEN. Every section used to be offered to everyone, so a
 * settings manager without AdvancedSettingsManagers was handed eight links to screens that
 * answer 403 — seven that loaded forever and one that showed a table of zeros. A link to
 * the other page is kept only for a reader holding that page's role; a group left with
 * nothing is dropped. The current page's own sections always stay: whoever is here is
 * either allowed, or already looking at the page's own refusal.
 *
 * @param {'main'|'advanced'} page
 * @param {string[]} [roles]  the reader's roles; omitted, nothing is filtered
 */
export function navFor(page, roles) {
  const holds = (role) => !roles || !role || roles.includes(role)

  return SETTINGS_NAV.map((group) => ({
    ...group,
    items: group.items
      .filter((item) => item.page === page || holds(item.role ?? PAGE_ROLE[item.page]))
      .map((item) => ({
        ...item,
        /* Every section has one address: its page, with the section in the hash. On the
           settings hub that hash is what opens the section's sheet — so a bookmark, an
           emailed link and the command search all land on the sheet they name. */
        to: item.to ?? `${PAGE_PATH[item.page]}#${item.id}`,
        local: item.page === page,
      })),
  })).filter((group) => group.items.length > 0)
}

/** A section's entry in the map, with the group it sits in — what its sheet is titled. */
export function findSection(id) {
  for (const group of SETTINGS_NAV) {
    const item = group.items.find((entry) => entry.id === id)
    if (item) return { item, group }
  }
  return { item: null, group: null }
}

/** The ids one page answers to in its URL hash. */
export function sectionIdsFor(page) {
  return SETTINGS_NAV.flatMap((group) =>
    group.items.filter((item) => item.page === page).map((item) => item.id),
  )
}

/** A section's label and one-line description, for the head of its panel. */
export function sectionFor(id) {
  const item = SETTINGS_NAV.flatMap((group) => group.items).find((entry) => entry.id === id)
  return { label: item?.label ?? '', about: N.about[id] ?? '' }
}
